/**
 * Forced-reflow + DOM mutation diagnostics.
 *
 * Activate by appending `?perf-debug=1` to any URL (or setting
 * `localStorage.PERF_DEBUG = "1"`). When inactive, this module is a no-op
 * and adds zero runtime cost beyond a single `if` check at startup.
 *
 * What it captures:
 *  1. JS call stacks every time code reads a layout-forcing geometry
 *     property (offsetWidth/Height/Top/Left, clientWidth/Height,
 *     scrollWidth/Height/Top/Left, getBoundingClientRect, getClientRects,
 *     getComputedStyle).
 *  2. DOM mutations via MutationObserver, with high-resolution timestamps,
 *     so you can correlate "DOM changed" → "layout read" sequences that
 *     produce forced reflows.
 *  3. PerformanceObserver entries for `longtask` and `layout-shift`.
 *
 * Output: a global `__perfDiag` object you can inspect in DevTools:
 *   __perfDiag.report()      → grouped table of layout-read call sites
 *   __perfDiag.mutations     → recent DOM mutations (ring buffer)
 *   __perfDiag.longTasks     → captured long tasks
 *   __perfDiag.layoutShifts  → captured layout shifts
 *   __perfDiag.reset()       → clear all buffers
 */

type LayoutReadSample = {
  api: string;
  stack: string;
  timeOrigin: number;
  sinceLastMutationMs: number | null;
};

type MutationSample = {
  type: MutationRecord["type"];
  target: string;
  addedNodes: number;
  removedNodes: number;
  attributeName: string | null;
  time: number;
};

type Diag = {
  enabled: boolean;
  reads: LayoutReadSample[];
  mutations: MutationSample[];
  longTasks: PerformanceEntry[];
  layoutShifts: PerformanceEntry[];
  lastMutationAt: number | null;
  report: () => void;
  reset: () => void;
};

declare global {
  interface Window {
    __perfDiag?: Diag;
  }
}

const MAX_SAMPLES = 500;

function isEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("perf-debug") === "1") return true;
    if (window.localStorage?.getItem("PERF_DEBUG") === "1") return true;
  } catch {
    // ignore (sandboxed iframe etc.)
  }
  return false;
}

function describeNode(node: Node | null): string {
  if (!node) return "null";
  if (node.nodeType === Node.ELEMENT_NODE) {
    const el = node as Element;
    const id = el.id ? `#${el.id}` : "";
    const cls =
      typeof el.className === "string" && el.className
        ? `.${el.className.split(/\s+/).slice(0, 2).join(".")}`
        : "";
    return `${el.tagName.toLowerCase()}${id}${cls}`;
  }
  return `#${node.nodeName}`;
}

function captureStack(): string {
  // Slice off the first two frames (Error + this function) for readability.
  const raw = new Error().stack ?? "";
  return raw.split("\n").slice(3, 12).join("\n");
}

function patchGetter(
  proto: object,
  name: string,
  diag: Diag,
  apiLabel: string,
) {
  const desc = Object.getOwnPropertyDescriptor(proto, name);
  if (!desc?.get) return;
  const original = desc.get;
  Object.defineProperty(proto, name, {
    ...desc,
    get(this: unknown) {
      record(diag, apiLabel);
      return original.call(this);
    },
  });
}

function patchMethod<T extends object>(
  proto: T,
  name: keyof T,
  diag: Diag,
  apiLabel: string,
) {
  const original = proto[name];
  if (typeof original !== "function") return;
  (proto as Record<string, unknown>)[name as string] = function (
    this: unknown,
    ...args: unknown[]
  ) {
    record(diag, apiLabel);
    return (original as (...a: unknown[]) => unknown).apply(this, args);
  };
}

function record(diag: Diag, api: string) {
  if (diag.reads.length >= MAX_SAMPLES) return;
  const now = performance.now();
  diag.reads.push({
    api,
    stack: captureStack(),
    timeOrigin: now,
    sinceLastMutationMs:
      diag.lastMutationAt != null ? now - diag.lastMutationAt : null,
  });
}

export function installPerfDiagnostics(): void {
  if (!isEnabled()) return;
  if (window.__perfDiag) return; // already installed

  const diag: Diag = {
    enabled: true,
    reads: [],
    mutations: [],
    longTasks: [],
    layoutShifts: [],
    lastMutationAt: null,
    report() {
      const buckets = new Map<
        string,
        { count: number; api: string; sample: LayoutReadSample }
      >();
      for (const r of diag.reads) {
        const key = `${r.api}::${r.stack.split("\n")[0] ?? ""}`;
        const existing = buckets.get(key);
        if (existing) existing.count += 1;
        else buckets.set(key, { count: 1, api: r.api, sample: r });
      }
      const rows = [...buckets.values()]
        .sort((a, b) => b.count - a.count)
        .map((b) => ({
          api: b.api,
          count: b.count,
          sinceLastMutationMs: b.sample.sinceLastMutationMs,
          firstFrame: b.sample.stack.split("\n")[0]?.trim() ?? "",
        }));
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        `[perf-diag] ${diag.reads.length} layout reads, ${diag.mutations.length} mutations, ${diag.longTasks.length} long tasks`,
      );
      // eslint-disable-next-line no-console
      console.table(rows);
      // eslint-disable-next-line no-console
      console.log("Full samples:", diag.reads);
      // eslint-disable-next-line no-console
      console.log("Mutations:", diag.mutations);
      // eslint-disable-next-line no-console
      console.log("Long tasks:", diag.longTasks);
      // eslint-disable-next-line no-console
      console.log("Layout shifts:", diag.layoutShifts);
      // eslint-disable-next-line no-console
      console.groupEnd();
    },
    reset() {
      diag.reads.length = 0;
      diag.mutations.length = 0;
      diag.longTasks.length = 0;
      diag.layoutShifts.length = 0;
      diag.lastMutationAt = null;
    },
  };

  window.__perfDiag = diag;

  // 1. Patch layout-forcing geometry APIs.
  const elProto = Element.prototype;
  const htmlProto = HTMLElement.prototype;
  patchGetter(htmlProto, "offsetWidth", diag, "offsetWidth");
  patchGetter(htmlProto, "offsetHeight", diag, "offsetHeight");
  patchGetter(htmlProto, "offsetTop", diag, "offsetTop");
  patchGetter(htmlProto, "offsetLeft", diag, "offsetLeft");
  patchGetter(elProto, "clientWidth", diag, "clientWidth");
  patchGetter(elProto, "clientHeight", diag, "clientHeight");
  patchGetter(elProto, "scrollWidth", diag, "scrollWidth");
  patchGetter(elProto, "scrollHeight", diag, "scrollHeight");
  patchGetter(elProto, "scrollTop", diag, "scrollTop");
  patchGetter(elProto, "scrollLeft", diag, "scrollLeft");
  patchMethod(elProto, "getBoundingClientRect", diag, "getBoundingClientRect");
  patchMethod(elProto, "getClientRects", diag, "getClientRects");

  const originalGetComputedStyle = window.getComputedStyle;
  window.getComputedStyle = function (...args: Parameters<typeof originalGetComputedStyle>) {
    record(diag, "getComputedStyle");
    return originalGetComputedStyle.apply(window, args);
  };

  // 2. DOM mutation observer.
  try {
    const mo = new MutationObserver((records) => {
      diag.lastMutationAt = performance.now();
      for (const r of records) {
        if (diag.mutations.length >= MAX_SAMPLES) break;
        diag.mutations.push({
          type: r.type,
          target: describeNode(r.target),
          addedNodes: r.addedNodes.length,
          removedNodes: r.removedNodes.length,
          attributeName: r.attributeName,
          time: performance.now(),
        });
      }
    });
    mo.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[perf-diag] MutationObserver failed:", err);
  }

  // 3. PerformanceObserver for long tasks and layout shifts.
  try {
    new PerformanceObserver((list) => {
      for (const e of list.getEntries()) diag.longTasks.push(e);
    }).observe({ type: "longtask", buffered: true });
  } catch {
    // longtask not supported in this browser
  }
  try {
    new PerformanceObserver((list) => {
      for (const e of list.getEntries()) diag.layoutShifts.push(e);
    }).observe({ type: "layout-shift", buffered: true });
  } catch {
    // layout-shift not supported
  }

  // Auto-report after page is fully idle so users get a result without
  // having to remember to call report().
  const autoReport = () => {
    setTimeout(() => diag.report(), 4000);
  };
  if (document.readyState === "complete") autoReport();
  else window.addEventListener("load", autoReport, { once: true });

  // eslint-disable-next-line no-console
  console.info(
    "[perf-diag] enabled. Inspect window.__perfDiag or call __perfDiag.report().",
  );
}
